'use client';

import { ChecklistItem } from '@app/app/_components/ChecklistItem/ChecklistItem';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { LifePackage } from '@app/domain/model/eshop/life/LifePackage';
import { determineCoverageName } from '@app/infrastructure/policies/determineCoverageName';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { PurchaseLifeInsuranceFormData } from '@app/service/eshop/life/actions/purchaseLifeInsurance';
import { Card } from '@app/app/_components/Card/Card';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { PaymentSchedule } from '@app/domain/model/eshop/life/PaymentSchedule';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { PaymentScheduleDetailsView } from '@app/app/esales/life/_sections/steps/PaymentScheduleDetailsView';
import { LifeCalculateResult } from '@app/domain/model/eshop/life/LifeCalculateResult';

interface Props {
    packages: LifePackage[];
    formData: PurchaseLifeInsuranceFormData;
    premium: Nullable<number>;
    onEditDefenses?: () => void;
    onEditContractInfo?: () => void;
    calculation: Nullable<LifeCalculateResult>;
}

interface CoverDisplay {
    code: string;
    sum: number;
    isAccident?: boolean;
    description?: string;
}

export function OfferSummaryStep({
    packages,
    formData,
    premium,
    onEditDefenses,
    onEditContractInfo,
    calculation,
}: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();
    const parameters = useAppParameters();

    const [selectedScheduleForDetails, setSelectedScheduleForDetails] = useState<Nullable<string>>(null);
    const [isDetailsViewOpen, setIsDetailsViewOpen] = useState(false);

    const selectedPackage = useMemo(() => {
        return packages.find(pkg => pkg.code === formData.selectedPackage);
    }, [packages, formData.selectedPackage]);

    const selectedCovers = useMemo<CoverDisplay[]>(() => {
        if (formData.selectedCustomCovers.length > 0) {
            return formData.selectedCustomCovers.map(cover => ({
                code: cover.code,
                sum: cover.sum,
                isAccident: false,
            }));
        }
        return (
            selectedPackage?.covers.map(cover => ({
                code: cover.code,
                sum: cover.default,
                isAccident: cover.isAccident,
                description: cover.description,
            })) ?? []
        );
    }, [formData.selectedCustomCovers, selectedPackage]);

    const nonAccidentCovers = useMemo(() => {
        return selectedCovers.filter(cover => !cover.isAccident);
    }, [selectedCovers]);

    const accidentCovers = useMemo(() => {
        return selectedCovers.filter(cover => cover.isAccident);
    }, [selectedCovers]);

    const getPaymentScheduleName = (schedule: string) => {
        switch (schedule) {
            case PaymentSchedule.Fixed:
                return t('eshop.life.fixedPayment');
            case PaymentSchedule.Increasing:
                return t('eshop.life.increasingPayment');
            case PaymentSchedule.Changing:
                return t('eshop.life.changingPayment');
            default:
                return schedule;
        }
    };

    const handlePaymentScheduleOpen = useCallback(() => {
        setSelectedScheduleForDetails(formData.paymentSchedule ?? null);
        setIsDetailsViewOpen(true);
    }, [formData.paymentSchedule]);

    const handleCloseDetailsView = useCallback(() => {
        setIsDetailsViewOpen(false);
        setSelectedScheduleForDetails(null);
    }, []);

    const conditionsHref = useMemo(() => {
        if (formData.paymentSchedule === PaymentSchedule.Fixed) {
            return parameters.eshopLifeInfoUrls.fixedPaymentConditions;
        } else {
            return parameters.eshopLifeInfoUrls.increasingPaymentConditions;
        }
    }, [
        formData.paymentSchedule,
        parameters.eshopLifeInfoUrls.fixedPaymentConditions,
        parameters.eshopLifeInfoUrls.increasingPaymentConditions,
    ]);

    return (
        <>
            <Card className="flex flex-col gap-4 rounded-xl border-2 border-primary-500 px-4 py-5 max-sm:mb-20">
                <CollapsablePanel
                    label={t('eshop.life.defenses')}
                    collapsable={false}
                    addon={
                        <TextButton color="primary" preventDefault onClick={onEditDefenses}>
                            {t('eshop.life.iWillChange')}
                        </TextButton>
                    }>
                    <div className="flex flex-col gap-2 px-6 py-4">
                        {nonAccidentCovers.map((cover, index) => (
                            <ChecklistItem key={`${cover.code}-${index}`}>
                                {`${t(determineCoverageName(cover.code))}: ${formatEuroValue(cover.sum, tenant, true)}`}
                            </ChecklistItem>
                        ))}
                        {accidentCovers.length > 0 && (
                            <>
                                <Paragraph>{t('eshop.life.packages.accident')}</Paragraph>
                                {accidentCovers.map((cover, index) => (
                                    <ChecklistItem key={`accident-${cover.code}-${index}`}>
                                        {cover.description ? (
                                            <span dangerouslySetInnerHTML={{ __html: cover.description }} />
                                        ) : (
                                            <span>{t(determineCoverageName(cover.code))}</span>
                                        )}
                                    </ChecklistItem>
                                ))}
                            </>
                        )}
                    </div>
                </CollapsablePanel>

                <CollapsablePanel
                    label={t('eshop.life.contractInformation')}
                    collapsable={false}
                    addon={
                        <TextButton color="primary" preventDefault onClick={onEditContractInfo}>
                            {t('eshop.life.iWillChange')}
                        </TextButton>
                    }>
                    <div className="flex flex-col gap-2 border-b border-neutral-300 px-6 pb-8 pt-4">
                        <Paragraph>
                            <span className="mr-1">
                                {t('eshop.life.contractDuration')}: {formData.insuranceDurationYears}
                            </span>
                            {t('general.units.years').toLowerCase()}
                        </Paragraph>
                        <Paragraph isBold>{getPaymentScheduleName(formData.paymentSchedule)}</Paragraph>
                    </div>
                </CollapsablePanel>
                <div className="flex flex-col gap-2 px-6">
                    {premium != null && (
                        <Paragraph isBold>
                            {t.rich('eshop.life.monthlyPaymentPerMonth', {
                                paymentAmount: formatEuroValue(premium, tenant),
                            })}
                        </Paragraph>
                    )}
                    <TextButton
                        href={conditionsHref}
                        target="_blank"
                        rightAddon={<ArrowRightIcon className="size-5" />}>
                        {t('eshop.life.conditions')}
                    </TextButton>
                    <TextButton
                        rightAddon={<ArrowRightIcon className="size-5" />}
                        preventDefault
                        onClick={handlePaymentScheduleOpen}>
                        {t('eshop.life.paymentSchedule')}
                    </TextButton>
                </div>
            </Card>
            <PaymentScheduleDetailsView
                paymentSchedule={selectedScheduleForDetails}
                calculation={calculation}
                open={isDetailsViewOpen}
                onClose={handleCloseDetailsView}
            />
        </>
    );
}
